import { memo } from 'react'

import { useDisclosure } from '@sequencehq/utils'
import { useReactFlow } from '@xyflow/react'
import { useSetAtom } from 'jotai'
import styled from 'styled-components'
import {
  GreyGrey30,
  GreyGrey60,
  GreyWhite,
  IconPlus,
  IndigoIndigo50,
  Popover,
  Portal,
  ShadowM
} from '@sequencehq/core-components'

import { insertNodeOnEdgeAtom } from 'Automations/automationEditor/atoms'
import { useInsertableNodeItems } from '../../hooks'
import { NodePickerBar } from './NodePickerBar'

/** Diameter of the circular "+" button; the edge centres it on its midpoint. */
export const EDGE_INSERT_BUTTON_SIZE = 22

const InsertButton = styled.button<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  width: ${EDGE_INSERT_BUTTON_SIZE}px;
  height: ${EDGE_INSERT_BUTTON_SIZE}px;
  border-radius: 9999px;
  background-color: ${GreyWhite};
  border: 1px solid ${({ $isOpen }) => ($isOpen ? IndigoIndigo50 : GreyGrey30)};
  color: ${({ $isOpen }) => ($isOpen ? IndigoIndigo50 : GreyGrey60)};
  box-shadow: ${ShadowM};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover,
  &:active {
    color: ${IndigoIndigo50};
    border-color: ${IndigoIndigo50};
  }
`

/**
 * The "+" affordance shown on an edge (on hover) that opens a node picker to
 * insert a node inline at that point in the flow. The picker mirrors the
 * bottom AddNodeToolbar (a horizontal bar of node icons) and sits 16px below
 * the button.
 *
 * The parent edge mounts this only while the edge is hovered or the picker is
 * open, so it's always shown when rendered. It reports its open state up via
 * `onOpenChange` so the edge keeps it mounted while the picker is open (which
 * closes on select or outside click). Memoised so it doesn't re-render if the
 * edge re-renders while it's mounted.
 */
export const EdgeInsertButton = memo(function EdgeInsertButton({
  edgeId,
  onOpenChange
}: {
  edgeId: string
  onOpenChange: (isOpen: boolean) => void
}) {
  const items = useInsertableNodeItems()
  const insertNode = useSetAtom(insertNodeOnEdgeAtom)
  const { getEdge, getNode } = useReactFlow()
  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen: () => onOpenChange(true),
    onClose: () => onOpenChange(false)
  })

  // The source node's measured width, used to keep the new node's input edge
  // visible (half the original edge) rather than butting against the source.
  const getSourceWidth = (): number => {
    const source = getEdge(edgeId)?.source
    return (source && getNode(source)?.measured?.width) || 0
  }

  return (
    <Popover.Root
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom"
      gutter={16}
      isLazy
    >
      <Popover.Trigger>
        <InsertButton
          type="button"
          $isOpen={isOpen}
          aria-label="Insert node"
          data-testid={`automations.editor.edge.${edgeId}.insertNode`}
          // Stop the button's own clicks reaching React Flow — otherwise it
          // selects the edge, elevating the line above the button.
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <IconPlus size={14} />
        </InsertButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Content
          width="auto"
          maxWidth="none"
          backgroundColor="transparent"
          border="none"
          boxShadow="none"
          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
        >
          <NodePickerBar
            items={items}
            itemTestId={nodeType =>
              `automations.editor.edge.insertNode.${nodeType}`
            }
            onSelect={item => {
              onClose()
              insertNode({
                edgeId,
                nodeType: item.nodeType,
                config: item.config,
                sourceWidth: getSourceWidth()
              })
            }}
          />
        </Popover.Content>
      </Portal>
    </Popover.Root>
  )
})
